import type { SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import {
  DISPLAY_STATUS_LABELS,
  worstStatus,
  type DisplayStatus,
  type ObservationUnit,
  type MonitorCheckRow,
  type VolumeBasis,
} from "@shared/types/monitor";
import { byCodeUnit } from "@shared/sort";

/**
 * The skyMonitor page's words and grouping (spec section 11). Pure, so the screen and its tests
 * share one definition. Status is always colour AND label (`DISPLAY_STATUS_LABELS`), never colour
 * alone.
 */

export const STATUS_COLORS = {
  healthy: "success",
  warning: "warning",
  failing: "danger",
  training: "informative",
  paused: "subtle",
  muted: "subtle",
  unknown: "subtle",
} as const satisfies Record<DisplayStatus, SkyBadgeColor>;

export function statusLabel(status: DisplayStatus): string {
  return DISPLAY_STATUS_LABELS[status];
}

/** "31 h", "12 min", "1,204 rows", "1 row", "2". Whole hours and minutes past ten; one decimal below. */
export function formatAmount(value: number | null, unit: ObservationUnit): string {
  if (value === null) return "Not recorded";
  if (unit === "rows") {
    const rows = Math.round(value);
    return `${rows.toLocaleString()} ${rows === 1 ? "row" : "rows"}`;
  }
  if (unit === "count") return Math.round(value).toLocaleString();
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded.toLocaleString()} ${unit === "hours" ? "h" : "min"}`;
}

/**
 * #1281: a row count named as the Tables tab names it (#1272, #1280): "56 rows loaded" (or "in the
 * last load" for the newest load) when it is the rows a load brought, "578 rows in total" when it
 * is the rows the table holds after the newest load, "578 rows in total after the load" for one
 * load of a history (`each`), since that total is not the table's now, as the Tables tab's "in
 * total after the load checked", and the bare count when skyData cannot tell.
 */
export function countWithBasis(
  value: number | null,
  unit: ObservationUnit,
  basis: VolumeBasis | null | undefined,
  load: "each" | "last" = "each",
): string {
  const amount = formatAmount(value, unit);
  if (value === null || unit !== "rows" || !basis) return amount;
  if (basis === "total")
    return load === "last" ? `${amount} in total` : `${amount} in total after the load`;
  return load === "last" ? `${amount} in the last load` : `${amount} loaded`;
}

/** "31 h / 26 h", or the value alone when there is no limit; failed runs in words (#829). */
export function valueAgainstLimit(
  row: Pick<MonitorCheckRow, "lastValue" | "expectedHigh" | "unit"> &
    Partial<Pick<MonitorCheckRow, "kind">>,
): string {
  if (row.kind === "failed_run") return failedRunsText(row.lastValue);
  const value = formatAmount(row.lastValue, row.unit);
  if (row.lastValue === null || row.expectedHigh === null) return value;
  return `${value} / ${formatAmount(row.expectedHigh, row.unit)}`;
}

/** #829: a `failed_run` check's value: "None failed", "1 failed run", "3 failed runs in a row". */
export function failedRunsText(value: number | null): string {
  if (value === null) return "No finished run";
  const runs = Math.round(value);
  if (runs === 0) return "None failed";
  return runs === 1 ? "1 failed run" : `${runs.toLocaleString()} failed runs in a row`;
}

/**
 * Round 10 wave 2 (A2b): why an `overdue_run` check is in breach by skipped firings. "Skipped 7
 * times: connection busy, held by Daily Azure SQL import"; null on any other row.
 */
export function skipDetailText(row: Pick<MonitorCheckRow, "skipDetail">): string | null {
  const detail = row.skipDetail;
  if (!detail) return null;
  const times = `Skipped ${detail.count.toLocaleString()} ${detail.count === 1 ? "time" : "times"}`;
  if (detail.reason === "previous_run_running") {
    return `${times}: the previous run was still running`;
  }
  const holder = detail.holderScheduleName ?? "an import started by hand";
  return `${times}: connection busy, held by ${holder}`;
}

/**
 * Round 6 W15: the hint for a table whose freshness reads Unknown because nothing says when it is
 * due: no schedule imports its connection and no fixed limit is set (`monitor/checks/freshness.ts`
 * answers `unknown` only then).
 */
export const NO_FRESHNESS_LIMIT_HINT = "No freshness limit: no schedule covers this connection";

/** Round 6 W15: the table's freshness has no limit because no schedule covers its connection. */
export function hasNoFreshnessLimit(table: Pick<WatchedTable, "freshness">): boolean {
  return table.freshness?.lastStatus === "unknown";
}

/** The Age / limit cell: "31 h / 26 h"; "12 h / no limit" when no schedule gives one (W15). */
export function freshnessText(check: MonitorCheckRow): string {
  if (check.lastStatus === "unknown" && check.expectedHigh === null && check.lastValue !== null) {
    return `${formatAmount(check.lastValue, check.unit)} / no limit`;
  }
  return valueAgainstLimit(check);
}

/** "1,204 rows (usual 900 to 1,100)", or the value alone while the band is unknown. */
export function valueInRange(
  row: Pick<MonitorCheckRow, "lastValue" | "expectedLow" | "expectedHigh" | "unit">,
): string {
  const value = formatAmount(row.lastValue, row.unit);
  if (row.lastValue === null || row.expectedLow === null || row.expectedHigh === null) return value;
  return `${value} (usual ${Math.round(row.expectedLow).toLocaleString()} to ${Math.round(
    row.expectedHigh,
  ).toLocaleString()})`;
}

/** One watched table: its checks, and the worst of their statuses. */
export interface WatchedTable {
  key: string;
  dataflowId: string;
  connectionName: string | null;
  entityName: string;
  /**
   * Round 6 W15: the warehouse table as the load recorded it; null before it landed where the
   * binding points.
   */
  tableName: string | null;
  /** What the Table column shows and sorts by: the warehouse table, else the entity's name. */
  displayName: string;
  status: DisplayStatus;
  freshness: MonitorCheckRow | null;
  volume: MonitorCheckRow | null;
  zeroRows: MonitorCheckRow | null;
  /** The newest signal of its checks: the table's last load. */
  lastLoadAt: string | null;
  lastEvalAt: string | null;
  checks: MonitorCheckRow[];
}

export function tablesOf(rows: readonly MonitorCheckRow[]): WatchedTable[] {
  const byKey = new Map<string, MonitorCheckRow[]>();
  for (const row of rows) {
    if (row.scope !== "table" || !row.dataflowId || !row.entityName) continue;
    const key = `${row.dataflowId}|${row.entityName}`;
    byKey.set(key, [...(byKey.get(key) ?? []), row]);
  }
  const newest = (values: Array<string | null>) =>
    values
      .filter((v): v is string => v !== null)
      .sort(byCodeUnit)
      .at(-1) ?? null;
  return [...byKey.entries()].map(([key, checks]) => {
    const first = checks[0]!;
    const tableName = checks.find((check) => check.tableName !== null)?.tableName ?? null;
    return {
      key,
      dataflowId: first.dataflowId!,
      connectionName: first.connectionName,
      entityName: first.entityName!,
      tableName,
      displayName: tableName ?? first.entityName!,
      status: worstStatus(checks.map((check) => check.status)),
      freshness: checks.find((check) => check.kind === "freshness") ?? null,
      volume: checks.find((check) => check.kind === "volume") ?? null,
      zeroRows: checks.find((check) => check.kind === "zero_rows") ?? null,
      lastLoadAt: newest(checks.map((check) => check.signalAt)),
      lastEvalAt: newest(checks.map((check) => check.lastEvalAt)),
      checks,
    };
  });
}

/** The schedule checks, one row each. */
export function scheduleChecksOf(rows: readonly MonitorCheckRow[]): MonitorCheckRow[] {
  return rows.filter((row) => row.scope === "schedule");
}
